import { IBillingInfo, useApi } from "../providers/ApiProvider"
import { useSkeem } from "../providers/SkeemProvider"
import { useEffect, useState } from "react"
import { useAsyncEffect } from "./useAsyncEffect"
import { useReloadKey } from "./useReloadKey"
import { Cache } from "../utils/cache"
import { useGlobalState } from "../utils/globalState"

export type SimpleBillingData = {
  freeAccount: boolean
  canAccessSystem: boolean

  // For a free account, these will be false/null
  hasPaymentMethod: boolean
  hadASubscription: boolean
  hasActiveSubscription: boolean
  hasSubscription: boolean
  subscriptionWillCancel: string | null
  nextPaymentDate: string | null
  subscriptionBegan: string | null
}

export function useCurrentBillingInfo(refetchOn: any[] = []) {
  const skeem = useSkeem()
  const api = useApi()
  const globalState = useGlobalState()

  const [reloadKey, triggerReload] = useReloadKey()
  const [merged, setMerged] = useState<SimpleBillingData | null>(null)

  useEffect(() => {
    const cleanup = skeem.on("session-change", triggerReload)
    return cleanup
  }, [])

  useEffect(() => {
    fetchUserDetails()
  }, [reloadKey, ...refetchOn])

  async function fetchUserDetails() {
    const billingInfo = await api.getCompanyBillingInfo(globalState.companyId)
    setMerged({
      ...billingInfo,
      freeAccount: billingInfo.hasFreeAccount,
      canAccessSystem: billingInfo.canAccessSystem,
      hasPaymentMethod: Boolean(billingInfo.paymentMethod),
    })
  }

  return merged
}

const companyBillingInfoCache = new Cache<IBillingInfo | "error">({ timeoutSeconds: 30 })

export function useCompanyBillingInfo(companyId?: string | null, refetchOn: any[] = []) {
  const api = useApi()
  const [data, setData] = useState<IBillingInfo | null>(null)

  useAsyncEffect(async () => {
    if (!companyId) {
      setData(null)
      return
    }

    const cached = companyBillingInfoCache.get(companyId)
    if (cached) {
      if (cached === "error") {
        console.error("Using cached data which was an error")
        setData(null)
        return
      }

      setData(cached)
      return
    }

    try {
      const data = await api.getCompanyBillingInfo(companyId)
      setData(data)
      companyBillingInfoCache.set(companyId, data)
    } catch (e) {
      console.log(e)
      companyBillingInfoCache.set(companyId, "error")
    }
  }, [companyId, ...refetchOn])

  return data
}
